import React from "react"
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks"
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, TextField } from "@mui/material";
import { IncidentCommentAddCommand } from "../../../../../services/varianceService/commands/IncidentCommentAddCommand";
import {
    useIncidentCommentAddMutation,
    useVarianceRejectMutation,
    useVarianceSaveMutation,
} from "../../../../../services/varianceService/variance-api";
import { toggleIncidentRejectCommentModal } from "../../../../../app/slices/VarianceModuleSlice";
import { useMyProfileQuery } from "../../../../../services/profileService/profile-api";
import { VariancePostCommand } from "../../../../../services/varianceService/commands/VariancePostCommand";
import { VarianceRejectPostCommand } from "../../../../../services/varianceService/commands/VarianceRejectPostCommand";

type ownProps = {
    setNotify: Function;
    setMessage: Function;
};

function IncidentRejectCommentDialog(props: ownProps) {
    const dispatch = useAppDispatch();
    const { data: profile } = useMyProfileQuery();
    const modalOpen = useAppSelector((state) => state.varianceModule.IncidentRejectCommentModalOpen);
    const incident = useAppSelector((state) => state.varianceRequest.varianceRequest);
    const [comment, setComment] = React.useState("");
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [saveError, setSaveError] = React.useState<string | null>(null);
    const [commentWarning, setCommentWarning] = React.useState(false);
    const [varianceSave] = useVarianceSaveMutation();
    const [saveStatus] = useVarianceRejectMutation();
    const [save] = useIncidentCommentAddMutation();

    const handleClose = () => {
        if (isSubmitting) return;
        dispatch(toggleIncidentRejectCommentModal());
        setComment('');
        setSaveError(null);
    };

    const saveComment = async () => {
        setSaveError(null);
        setIsSubmitting(true);
        try {
            await varianceSave(new VariancePostCommand(incident)).unwrap();
            const rejectCommand = new VarianceRejectPostCommand(incident.id, profile!.identity!, profile!.email!);
            await saveStatus(rejectCommand).unwrap();
        } catch {
            setSaveError('Rejection failed. Please try again.');
            setIsSubmitting(false);
            return;
        }
        if (comment.trim()) {
            try {
                await save(new IncidentCommentAddCommand(incident.id!, comment, profile!.identity!)).unwrap();
            } catch {
                setCommentWarning(true);
            }
        }
        setIsSubmitting(false);
        dispatch(toggleIncidentRejectCommentModal());
        setComment('');
        props.setNotify(true);
        props.setMessage('Incident Rejected');
    };

    return (
        <React.Fragment>
            <Dialog open={modalOpen} onClose={handleClose}>
                <DialogTitle>Reason for Rejection</DialogTitle>
                <DialogContent>
                    <TextField
                        label='Comment'
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        multiline
                        fullWidth
                        sx={{ width: '500px', marginTop: '10px' }}
                        minRows='3'
                        disabled={isSubmitting}
                    />
                    {saveError && (
                        <Alert severity="error" sx={{ marginTop: '10px' }}>
                            {saveError}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={isSubmitting}>Cancel</Button>
                    <Button
                        onClick={saveComment}
                        disabled={isSubmitting}
                        startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={commentWarning}
                autoHideDuration={6000}
                onClose={() => setCommentWarning(false)}
            >
                <Alert severity="warning" onClose={() => setCommentWarning(false)}>
                    Incident was rejected, but the comment could not be saved. You may add it manually.
                </Alert>
            </Snackbar>
        </React.Fragment>
    );
}

export { IncidentRejectCommentDialog }